import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import client from '../api/client';

export default function SetPassword() {
  const { brand }        = useBrand();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();

  const primary = brand.primary_color || '#0099FF';
  const dark    = brand.dark_color    || '#151515';
  const logoUrl = brand.login_logo_url || brand.logo_url || '';

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [checking,  setChecking]  = useState(true);
  const [tokenValid,setTokenValid]= useState(false);

  // ── Read token from URL: /set-password?token=XXXX ──────────
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    // Verify the invite token is valid before showing the form
    client.get(`/api/users/verify-invite?token=${token}`)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');
    if (!token) return setError('Invalid invite link. Please request a new one.');

    setLoading(true);
    try {
      // Send token + new password to backend — no Supabase client needed
      await client.post('/api/users/set-password', { token, password });
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const strength      = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : 3;
  const strengthColor = ['transparent', '#EF4444', '#F59E0B', '#22C55E'][strength];
  const strengthLabel = ['', 'Weak', 'Fair', 'Strong'][strength];

  return (
    <div style={{ minHeight:'100vh', background:dark, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',system-ui,sans-serif" }}>
      <div style={{ width:440, background:'#fff', borderRadius:20, padding:40, boxSizing:'border-box', boxShadow:'0 25px 50px rgba(0,0,0,0.4)' }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          {logoUrl ? (
            <img src={logoUrl} alt={brand.app_name || 'Logo'}
              style={{ maxHeight:60, maxWidth:'80%', objectFit:'contain', display:'block', margin:'0 auto 14px' }} />
          ) : (
            <div style={{ width:52, height:52, borderRadius:14, background:primary, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <i className="ti ti-bolt" style={{ fontSize:26, color:'#fff' }} />
            </div>
          )}
        </div>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ width:48, height:48, borderRadius:'50%', background:'#f0f9ff', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
            <i className="ti ti-lock-open" style={{ fontSize:24, color:primary }} />
          </div>
          <div style={{ fontWeight:600, fontSize:17, color:dark, marginBottom:6 }}>Set Your Password</div>
          <div style={{ fontSize:13, color:'#6b7280' }}>
            You've been invited! Choose a password to activate your account.
          </div>
        </div>

        {/* States */}
        {checking ? (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            <div style={{ width:28, height:28, border:`3px solid #e5e7eb`, borderTop:`3px solid ${primary}`, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 10px' }} />
            <div style={{ fontSize:13, color:'#6b7280' }}>Verifying invite link...</div>
          </div>

        ) : !token || !tokenValid ? (
          <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:16, textAlign:'center' }}>
            <i className="ti ti-alert-circle" style={{ fontSize:28, color:'#EF4444', display:'block', marginBottom:8 }} />
            <div style={{ fontWeight:600, fontSize:14, color:'#991b1b', marginBottom:4 }}>Invite Link Expired</div>
            <div style={{ fontSize:13, color:'#7f1d1d' }}>
              This invite link has expired or is invalid. Please ask your admin to resend the invite.
            </div>
          </div>

        ) : (
          <form onSubmit={handleSetPassword}>
            {/* New password */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:500, color:'#374151', marginBottom:6, display:'block' }}>New Password</label>
              <div style={{ position:'relative' }}>
                <i className="ti ti-lock" style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:16 }} />
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ ...inp, paddingLeft:34, paddingRight:38 }}
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9ca3af', padding:0 }}>
                  <i className={`ti ${showPass ? 'ti-eye-off' : 'ti-eye'}`} style={{ fontSize:16 }} />
                </button>
              </div>
              {password.length > 0 && (
                <div style={{ marginTop:6 }}>
                  <div style={{ display:'flex', gap:4, marginBottom:3 }}>
                    {[1,2,3].map(i => (
                      <div key={i} style={{ flex:1, height:3, borderRadius:2, background:strength>=i?strengthColor:'#e5e7eb', transition:'background 0.2s' }} />
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:strengthColor }}>{strengthLabel} password</div>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:500, color:'#374151', marginBottom:6, display:'block' }}>Confirm Password</label>
              <div style={{ position:'relative' }}>
                <i className="ti ti-lock-check" style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:16 }} />
                <input
                  type={showPass ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  style={{ ...inp, paddingLeft:34 }}
                />
                {confirm.length > 0 && (
                  <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)' }}>
                    {confirm === password
                      ? <i className="ti ti-circle-check" style={{ fontSize:16, color:'#22C55E' }} />
                      : <i className="ti ti-circle-x"     style={{ fontSize:16, color:'#EF4444' }} />
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ color:'#EF4444', fontSize:12, marginBottom:12, background:'#fef2f2', borderRadius:8, padding:'8px 12px', display:'flex', alignItems:'center', gap:6 }}>
                <i className="ti ti-alert-circle" style={{ fontSize:14 }} />{error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || password !== confirm || password.length < 8}
              style={{ width:'100%', padding:'11px 16px', borderRadius:8, border:'none', background:primary, color:'#fff', fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:loading||password!==confirm||password.length<8?0.7:1, transition:'opacity 0.15s' }}>
              {loading ? (
                <>
                  <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.4)', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                  Activating account...
                </>
              ) : <>Activate My Account <i className="ti ti-arrow-right" style={{ fontSize:15 }} /></>}
            </button>

            {/* Requirements */}
            <div style={{ marginTop:14, padding:'10px 14px', background:'#f9fafb', borderRadius:8, fontSize:12, color:'#6b7280' }}>
              <div style={{ fontWeight:500, marginBottom:6 }}>Password requirements:</div>
              {[
                { label:'At least 8 characters', met:password.length>=8 },
                { label:'Passwords match',        met:password===confirm&&confirm.length>0 },
              ].map((r,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                  <i className={`ti ${r.met?'ti-circle-check':'ti-circle-x'}`} style={{ fontSize:13, color:r.met?'#22C55E':'#d1d5db' }} />
                  <span style={{ color:r.met?'#374151':'#9ca3af' }}>{r.label}</span>
                </div>
              ))}
            </div>
          </form>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
